import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Trash2, Plus, Pencil, X } from 'lucide-react';
import { api } from '../../../context/AuthContext';
import StatusPill from '../StatusPill';
import { toast } from 'sonner';
import ProjectPlanPanel from './ProjectPlanPanel';

const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const dateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Project updates: the operating timeline investors see on their investment.
 */
export const ProjectUpdatesTab = ({ projectId }) => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', publish: true, date: today() });
  const [editing, setEditing] = useState(null);

  const fetchUpdates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/v2/projects/${projectId}/updates`);
      setUpdates(res.data.updates || []);
    } catch {
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/v2/projects/${projectId}/updates`, form);
      toast.success(form.publish ? 'Update published' : 'Update saved as draft');
      setForm({ title: '', content: '', publish: true, date: today() });
      fetchUpdates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add update');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing.title.trim() || !editing.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/v2/projects/${projectId}/updates/${editing.id}`, {
        title: editing.title,
        content: editing.content,
        date: editing.date || null,
        is_published: editing.is_published,
      });
      toast.success('Update saved');
      setEditing(null);
      fetchUpdates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this update? Investors will no longer see it.')) return;
    try {
      await api.delete(`/admin/v2/projects/${projectId}/updates/${id}`);
      toast.success('Update deleted');
      fetchUpdates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete update');
    }
  };

  return (
    <div className="space-y-4 md:space-y-5" data-testid="project-updates-tab">
      <ProjectPlanPanel projectId={projectId} />

      <div className="grid gap-4 md:gap-5 lg:grid-cols-[1fr_360px]">
        <section className="bg-white rounded-xl border border-black/[0.06]">
          <div className="px-5 py-3.5 border-b border-black/[0.05] flex items-center justify-between">
            <h3 className="text-sm font-medium">Operating timeline</h3>
            <span className="text-xs text-foreground-muted">{updates.length} updates</span>
          </div>

          {loading ? (
            <div className="py-14 flex justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-t-2 border-b-2 border-primary" />
            </div>
          ) : updates.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="updates-empty">
              <p className="text-sm font-medium">No project updates</p>
              <p className="text-xs text-foreground-muted mt-1">
                Keep investors informed about project progress - the first update starts the timeline.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-black/[0.05]">
              {updates.map((u) => (
                <div key={u.id} className="px-5 py-4" data-testid={`admin-update-${u.id}`}>
                  {editing?.id === u.id ? (
                    <div className="space-y-3">
                      <Input
                        value={editing.title}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        data-testid="edit-update-title-input"
                      />
                      <Textarea
                        value={editing.content}
                        rows={5}
                        onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                        data-testid="edit-update-content-input"
                      />
                      <div className="flex flex-wrap items-center gap-4">
                        <div>
                          <Label className="text-[11px]">Date of update</Label>
                          <Input
                            type="date"
                            value={editing.date}
                            onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                            className="mt-1"
                            data-testid="edit-update-date-input"
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-4">
                          <Switch
                            checked={editing.is_published}
                            onCheckedChange={(v) => setEditing({ ...editing, is_published: v })}
                            data-testid="edit-update-publish-switch"
                          />
                          <Label className="text-sm">Visible to investors</Label>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSaveEdit} disabled={saving} data-testid="save-update-btn">
                          {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="cancel-edit-update-btn">
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{u.title}</p>
                          <StatusPill
                            status={u.is_published ? 'published' : 'draft'}
                            label={u.is_published ? 'Published' : 'Draft'}
                          />
                        </div>
                        <p className="text-xs text-foreground-muted mt-0.5">{shortDate(u.date)}</p>
                        <p className="text-sm text-foreground-muted mt-2 whitespace-pre-line">{u.content}</p>
                      </div>
                      <div className="flex items-center">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-foreground-muted hover:text-slate-900"
                          onClick={() => setEditing({
                            id: u.id,
                            title: u.title || '',
                            content: u.content || '',
                            date: dateInput(u.date),
                            is_published: !!u.is_published,
                          })}
                          data-testid={`edit-update-${u.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-foreground-muted hover:text-red-600"
                          onClick={() => handleDelete(u.id)}
                          data-testid={`delete-update-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-black/[0.06] h-fit">
          <div className="px-5 py-3.5 border-b border-black/[0.05]">
            <h3 className="text-sm font-medium">New update</h3>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Renovation works underway"
                className="mt-1"
                data-testid="update-title-input"
              />
            </div>
            <div>
              <Label>Date of update</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1"
                data-testid="update-date-input"
              />
            </div>
            <div>
              <Label>Update</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={6}
                placeholder="What progressed, what is next, any key dates…"
                className="mt-1"
                data-testid="update-content-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.publish}
                onCheckedChange={(v) => setForm({ ...form, publish: v })}
                data-testid="update-publish-switch"
              />
              <Label className="text-sm">Visible to investors</Label>
            </div>
            <Button className="w-full" onClick={handleAdd} disabled={saving} data-testid="add-update-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {saving ? 'Saving…' : 'Add update'}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProjectUpdatesTab;
